import { useEffect, useMemo, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
} from '@xyflow/react';
import type { Edge, NodeTypes } from '@xyflow/react';
import type { DagNodeState, WorkflowStepStatus } from '@/lib/types';
import type { DagNode } from '@/lib/api';
import { dagNodesToReactFlow, resolveNodeDisplay } from '@/lib/dag-layout';
import { formatDurationMs } from '@/lib/format';
import {
  executionDagNode,
  type ExecutionFlowNode,
  type ExecutionNodeData,
} from './ExecutionDagNode';

import '@xyflow/react/dist/style.css';

// Defined at module scope — prevents ReactFlow from remounting nodes on every render
const nodeTypes: NodeTypes = { executionNode: executionDagNode };

const STATUS_MINIMAP_COLORS: Partial<Record<WorkflowStepStatus, string>> = {
  completed: 'var(--success)',
  running: 'var(--accent-bright)',
  failed: 'var(--error)',
  skipped: 'var(--text-tertiary)',
};
const DEFAULT_MINIMAP_COLOR = 'var(--surface-elevated)';

const EDGE_STROKE_BY_STATUS: Partial<Record<WorkflowStepStatus, string>> = {
  completed: 'var(--success)',
  running: 'var(--accent-bright)',
  failed: 'var(--error)',
};
const DEFAULT_EDGE_STROKE = 'var(--border)';

function useNowTicker(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const interval = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return (): void => {
      window.clearInterval(interval);
    };
  }, [active]);
  return now;
}

interface WorkflowDagViewerProps {
  dagNodes: readonly DagNode[];
  liveStatus: readonly DagNodeState[];
  isRunning: boolean;
  currentlyExecuting?: { nodeName: string; startedAt: number };
  selectedNodeId?: string | null;
  onNodeClick?: (nodeId: string) => void;
}

export function WorkflowDagViewer({
  dagNodes,
  liveStatus,
  isRunning,
  currentlyExecuting,
  selectedNodeId,
  onNodeClick,
}: WorkflowDagViewerProps): React.ReactElement {
  const now = useNowTicker(isRunning && currentlyExecuting !== undefined);

  // Compute topology layout ONCE from the workflow definition.
  // Only re-layout when the definition changes (node/edge count), not on status updates.
  const { baseNodes, edges: layoutedEdges } = useMemo(() => {
    const { nodes, edges } = dagNodesToReactFlow(dagNodes);
    return { baseNodes: nodes, edges };
  }, [dagNodes]);

  // Build a status lookup map from live SSE/REST data
  const statusMap = useMemo(() => {
    const map = new Map<string, DagNodeState>();
    for (const node of liveStatus) {
      map.set(node.nodeId, node);
    }
    return map;
  }, [liveStatus]);

  // Overlay live status onto the topology nodes.
  // Creates new node objects only for nodes whose status changed (React.memo handles the rest).
  const nodes: ExecutionFlowNode[] = useMemo(() => {
    return baseNodes.map(node => {
      const live = statusMap.get(node.id);
      // baseNodes is derived from dagNodes, so this find should always succeed
      const dagNode = dagNodes.find(dn => dn.id === node.id);
      const display = dagNode ? resolveNodeDisplay(dagNode) : node.data;
      return {
        ...node,
        type: 'executionNode',
        data: {
          ...node.data,
          ...display,
          status: live?.status,
          duration: live?.duration,
          error: live?.error,
          selected: node.id === selectedNodeId,
          currentIteration: live?.currentIteration,
          maxIterations: live?.maxIterations,
        },
      } as ExecutionFlowNode;
    });
  }, [baseNodes, statusMap, dagNodes, selectedNodeId]);

  // Color edges based on target node status
  const edges: Edge[] = useMemo(() => {
    return layoutedEdges.map(edge => {
      const targetStatus = statusMap.get(edge.target)?.status;
      const stroke = (targetStatus && EDGE_STROKE_BY_STATUS[targetStatus]) ?? DEFAULT_EDGE_STROKE;
      return {
        ...edge,
        animated: targetStatus === 'running',
        // ReactFlow SVG edges require inline style for stroke — className cannot target SVG stroke.
        style: { stroke, strokeWidth: 1.5 },
      };
    });
  }, [layoutedEdges, statusMap]);

  return (
    <div className="relative h-full w-full bg-surface-inset">
      {/* Atmospheric sky glow at top — matches the design's `T.glowSky`
       * overlay on the DAG canvas. Sits behind React Flow content. */}
      <span
        aria-hidden
        className="fd-glow-sky pointer-events-none absolute inset-0 z-0 opacity-50"
      />

      {/* Currently-executing bar — design shows a sky-tinted strip at the
       * bottom of the canvas with the running node's name + sweep ring +
       * step info. Position it as a wide bar (was a top-right pill). */}
      {isRunning && currentlyExecuting && (
        <div className="pointer-events-none absolute inset-x-4 bottom-4 z-10 flex flex-wrap items-center gap-3 rounded-lg border border-accent-bright/25 bg-[oklch(0.72_0.14_225/0.06)] px-3.5 py-2.5 backdrop-blur-sm">
          <span className="fd-sweep size-3 shrink-0" />
          <span className="font-mono text-xs font-semibold text-accent-bright">
            {currentlyExecuting.nodeName}
          </span>
          <span className="font-mono text-[11px] text-text-secondary">
            elapsed {formatDurationMs(now - currentlyExecuting.startedAt)}
          </span>
        </div>
      )}

      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={true}
          onNodeClick={
            onNodeClick
              ? (_event, node): void => {
                  onNodeClick(node.id);
                }
              : undefined
          }
          fitView
          fitViewOptions={{ padding: 0.15 }}
          panOnDrag
          zoomOnScroll
          className="bg-transparent"
        >
          <Background
            variant={BackgroundVariant.Dots}
            gap={24}
            size={1}
            color="oklch(1 0 0 / 0.06)"
          />
          <Controls
            showInteractive={false}
            className="!overflow-hidden !rounded-md !border !border-border !bg-surface !shadow-[0_1px_2px_oklch(0_0_0/0.4)] [&_button]:!border-border [&_button]:!bg-surface [&_button]:!text-text-secondary [&_button:hover]:!bg-surface-elevated [&_button:hover]:!text-text-primary"
          />
          <MiniMap
            nodeColor={(node): string => {
              const data = node.data as ExecutionNodeData;
              return (data.status && STATUS_MINIMAP_COLORS[data.status]) ?? DEFAULT_MINIMAP_COLOR;
            }}
            className="!rounded-md !border !border-border !bg-surface !shadow-[0_1px_2px_oklch(0_0_0/0.4)]"
            maskColor="oklch(0 0 0 / 0.55)"
          />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
}
